/**
 * RevenueCard - Premium metric display card for financial data
 *
 * Features:
 * - Large, prominent value display with tabular numbers
 * - Trend indicator with percentage change
 * - Gradient accent based on metric type
 * - Smooth hover animation
 */
import { cn } from '@respectly/ui';
import { Card, CardContent } from '@respectly/ui';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { motion } from 'framer-motion';

// ============================================
// Types
// ============================================

interface RevenueCardProps {
  title: string;
  value: number;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  icon: React.ReactNode;
  format?: 'currency' | 'percentage' | 'number';
  description?: string;
  className?: string;
}

// ============================================
// Formatters
// ============================================

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const currencyFormatterCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const numberFormatter = new Intl.NumberFormat('en-US');

function formatValue(value: number, format: 'currency' | 'percentage' | 'number'): string {
  switch (format) {
    case 'currency':
      // Use compact format for large numbers
      if (Math.abs(value) >= 10000) {
        return currencyFormatterCompact.format(value);
      }
      return currencyFormatter.format(value);
    case 'percentage':
      return percentFormatter.format(value / 100);
    case 'number':
      return numberFormatter.format(value);
    default:
      return String(value);
  }
}

// ============================================
// Component
// ============================================

export function RevenueCard({
  title,
  value,
  trend,
  icon,
  format = 'currency',
  description,
  className,
}: RevenueCardProps) {
  const formattedValue = formatValue(value, format);
  const isPositiveTrend = trend?.isPositive ?? true;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={{ y: -2 }}
      className={className}
    >
      <Card className="relative overflow-hidden border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900 shadow-sm hover:shadow-md transition-shadow duration-300">
        {/* Subtle gradient accent at top */}
        <div
          className={cn(
            'absolute inset-x-0 top-0 h-1',
            isPositiveTrend
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-500'
              : 'bg-gradient-to-r from-red-400 to-red-500'
          )}
        />

        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              {/* Title */}
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                {title}
              </p>

              {/* Value */}
              <p className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white tabular-nums">
                {formattedValue}
              </p>

              {/* Trend */}
              {trend && (
                <div className="flex items-center gap-1.5 mt-2">
                  {trend.value === 0 ? (
                    <>
                      <Minus className="h-4 w-4 text-slate-400" />
                      <span className="text-sm font-medium text-slate-500">
                        No change
                      </span>
                    </>
                  ) : trend.isPositive ? (
                    <>
                      <TrendingUp className="h-4 w-4 text-emerald-500" />
                      <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                        +{Math.abs(trend.value).toFixed(1)}%
                      </span>
                    </>
                  ) : (
                    <>
                      <TrendingDown className="h-4 w-4 text-red-500" />
                      <span className="text-sm font-medium text-red-600 dark:text-red-400">
                        -{Math.abs(trend.value).toFixed(1)}%
                      </span>
                    </>
                  )}
                  <span className="text-xs text-slate-400 ml-1">vs last month</span>
                </div>
              )}

              {/* Description */}
              {description && (
                <p className="text-xs text-slate-400 mt-2">{description}</p>
              )}
            </div>

            {/* Icon */}
            <div
              className={cn(
                'flex-shrink-0 p-3 rounded-xl',
                isPositiveTrend
                  ? 'bg-emerald-50 dark:bg-emerald-900/20'
                  : 'bg-red-50 dark:bg-red-900/20'
              )}
            >
              <div
                className={cn(
                  isPositiveTrend
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-red-600 dark:text-red-400'
                )}
              >
                {icon}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ============================================
// Skeleton
// ============================================

export function RevenueCardSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardContent className="p-5">
        <div className="animate-pulse">
          <div className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
          <div className="h-9 w-28 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
          <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
        </div>
      </CardContent>
    </Card>
  );
}
